import { DestroyRef, Directive, ElementRef, afterNextRender, inject, input } from '@angular/core';
import { isMacOS, isTauri } from '../../core/tauri-runtime';
import { DRAG_REGION_ATTR, dragSurfaces } from './desktop-drag-region.logic';

/**
 * imDesktopDragRegion — the macOS desktop window moves when you drag the
 * empty parts of a bar, and zooms when you double-click them.
 *
 * RESPONSIBILITY
 *   Tag the bar's own empty surfaces (`dragSurfaces`) with Tauri's bare
 *   `data-tauri-drag-region`, and keep the tags current as the bar re-renders
 *   (tiles collapsing to a pill, chips appearing). Tauri's injected script
 *   does the rest: drag on mousedown, zoom on a still double-click.
 *
 * SCOPE
 *   Only inside Tauri on macOS, where the window's overlay title bar
 *   (`titleBarStyle: "Overlay"`) puts the page under the traffic lights.
 *   On the web and on Windows/Linux (native frame) it does nothing.
 *
 * NEEDS
 *   `core:window:allow-start-dragging` on the window's capability
 *   (src-tauri/capabilities/default.json). Without it every press logs
 *   "window.start_dragging not allowed" and the window stays put. The zoom
 *   uses `internal_toggle_maximize`, which `core:default` already allows.
 *
 * USED BY
 *   - app-shell.component.html — Paper's shell bar (its `paper-glass-bar`)
 *   - soft-frost-rail.component.html — the workspace nav's header (Home, Flow)
 *
 * @example
 * <!-- the value is a selector for the bar inside the host; empty = the host -->
 * <paper-shell-bar imDesktopDragRegion="paper-glass-bar" … />
 * <header class="frost-header" imDesktopDragRegion>…</header>
 */
@Directive({
  selector: '[imDesktopDragRegion]',
  standalone: true,
})
export class DesktopDragRegionDirective {
  /** CSS selector for the bar element inside the host. Empty means the host itself. */
  readonly barSelector = input<string>('', { alias: 'imDesktopDragRegion' });

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tagged = new Set<HTMLElement>();

  constructor() {
    if (!isTauri || !isMacOS) return;
    afterNextRender(() => {
      const root = this.host.nativeElement;
      let queued = false;
      const sync = (): void => {
        queued = false;
        this.retag();
      };
      // Child-list changes only: the tags this writes are attributes, so the
      // observer never wakes itself.
      const observer = new MutationObserver(() => {
        if (queued) return;
        queued = true;
        queueMicrotask(sync);
      });
      observer.observe(root, { childList: true, subtree: true });
      this.retag();
      this.destroyRef.onDestroy(() => {
        observer.disconnect();
        for (const el of this.tagged) el.removeAttribute(DRAG_REGION_ATTR);
        this.tagged.clear();
      });
    });
  }

  /** Tag what is a surface now; untag only what this directive tagged itself (a template's own attribute stays). */
  private retag(): void {
    const root = this.host.nativeElement;
    const selector = this.barSelector().trim();
    const bar = selector ? root.querySelector(selector) : root;
    const next = new Set(bar ? dragSurfaces(bar) : []);
    for (const el of [...this.tagged]) {
      if (next.has(el)) continue;
      el.removeAttribute(DRAG_REGION_ATTR);
      this.tagged.delete(el);
    }
    for (const el of next) {
      if (el.hasAttribute(DRAG_REGION_ATTR)) continue;
      el.setAttribute(DRAG_REGION_ATTR, '');
      this.tagged.add(el);
    }
  }
}
